import React from 'react';
import { Box, Typography, Grid, Paper, Chip, Tooltip } from '@mui/material';
import { toBlanks } from '../../utils/textHolesBlanks';

// Badge « mode écoute » pour les jeux de tri gestuels lus (homophones).
const SpokenModeBadge = ({ content }) =>
  content?.gestureStimulus === 'spoken'
    ? <Chip label="🔊 Mode écoute" size="small" color="info" variant="outlined" sx={{ mb: 1 }} />
    : null;

// Item de tri : mot-réponse + contexte de désambiguïsation (spoken/written) si présent.
const GestureItemChip = ({ item }) => {
  const ctx = [
    item?.writtenContext ? `✍️ ${item.writtenContext}` : null,
    item?.spokenContext ? `🔊 ${item.spokenContext}` : null,
  ].filter(Boolean).join('  ·  ');

  const chip = (
    <Chip
      label={item?.text}
      size="small"
      color={ctx ? 'primary' : 'default'}
      variant={ctx ? 'outlined' : 'filled'}
    />
  );
  return ctx ? <Tooltip title={ctx} arrow>{chip}</Tooltip> : chip;
};

const ExercisePreview = ({ gameType, content }) => {
  if (!content) return <Typography color="text.secondary">Pas de contenu à afficher</Typography>;

  switch (gameType) {
    case 'speed_game': {
      const words = content.words || [];
      return (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {words.length} mot{words.length > 1 ? 's' : ''}
          </Typography>
          <Grid container spacing={1}>
            {words.slice(0, 10).map((word, index) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight="bold">{word.correct}</Typography>
                  <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                    {(word.options || []).map((option, idx) => (
                      <Chip
                        key={idx}
                        label={option}
                        size="small"
                        color={option === word.correct ? 'success' : 'default'}
                      />
                    ))}
                  </Box>
                  {word.usagePhrase && (
                    <Typography variant="caption" display="block" mt={0.5} fontStyle="italic" color="text.secondary">
                      {word.usagePhrase}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
            {words.length > 10 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  +{words.length - 10} autres mots…
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }

    case 'text_holes': {
      const blanks = toBlanks(content);
      return (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" whiteSpace="pre-wrap">{content.text}</Typography>
          </Paper>
          {content.completedText && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
              Complété : {content.completedText}
            </Typography>
          )}
          {blanks.length > 0 ? (
            blanks.map((blank, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Trou {i + 1}{blank.type ? ` (${blank.type})` : ''}
                </Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                  <Chip label={blank.answer} size="small" color="success" />
                  {(blank.distractors || []).map((d, j) => (
                    <Chip key={j} label={d} size="small" color="warning" variant="outlined" />
                  ))}
                </Box>
              </Paper>
            ))
          ) : (
            <Typography variant="caption" color="error">
              Aucun trou défini (blanks[] vide)
            </Typography>
          )}
        </Box>
      );
    }

    case 'correction_game': {
      const isIdentify = content.interactionMode === 'identify'
        || (Array.isArray(content.targets) && content.targets.length > 0);
      if (isIdentify) {
        return (
          <Box>
            {content.enonce && (
              <Typography variant="subtitle2" gutterBottom color="primary">
                {content.enonce}
              </Typography>
            )}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body2" whiteSpace="pre-wrap">{content.content}</Typography>
            </Paper>
            <Chip label="Mode repérage" size="small" color="info" sx={{ mb: 1 }} />
            {(content.targets || []).map((target, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip label={target.text} size="small" color="primary" />
                  {target.role && <Chip label={target.role} size="small" variant="outlined" />}
                </Box>
                {target.explanation && (
                  <Typography variant="caption" display="block" mt={0.5} color="text.secondary">
                    {target.explanation}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        );
      }
      return (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" whiteSpace="pre-wrap">{content.content}</Typography>
          </Paper>
          {(content.errors || []).map((error, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Chip label={error.text} size="small" color="error" />
                <Typography variant="caption">→</Typography>
                <Chip label={error.correction} size="small" color="success" />
                <Chip label={error.type} size="small" variant="outlined" />
              </Box>
              {error.explanation && (
                <Typography variant="caption" display="block" mt={0.5} color="text.secondary">
                  {error.explanation}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      );
    }

    case 'word_lists':
      return (
        <Box>
          {(content.words || []).map((item, i) => {
            const row = typeof item === 'string' ? { word: item } : item;
            return (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">{row.word}</Typography>
              {row.example && (
                <Typography variant="caption" display="block" fontStyle="italic" color="text.secondary">
                  {row.example}
                </Typography>
              )}
              {row.usagePhrase && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {row.usagePhrase}
                </Typography>
              )}
              {row.hint && (
                <Typography variant="caption" display="block" color="primary.main">
                  💡 {row.hint}
                </Typography>
              )}
            </Paper>
            );
          })}
        </Box>
      );

    case 'dictation':
    case 'word_choice': {
      const dictationText = content.text || content.content || '';
      const wordChoices = Array.isArray(content.wordChoices) ? content.wordChoices : [];
      return (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" whiteSpace="pre-wrap">{dictationText}</Typography>
          </Paper>
          {wordChoices.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Dictée guidée — {wordChoices.length} mot(s)
              </Typography>
              {wordChoices.slice(0, 8).map((row) => (
                <Paper key={row.wordIndex} variant="outlined" sx={{ p: 1, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Mot {row.wordIndex + 1}
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                    {(row.options || []).map((opt, i) => (
                      <Chip
                        key={i}
                        label={opt}
                        size="small"
                        color={i === 0 ? 'success' : 'default'}
                        variant={i === 0 ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
              {wordChoices.length > 8 && (
                <Typography variant="caption" color="text.secondary">
                  +{wordChoices.length - 8} autres mots…
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Dictée simple (texte seul, sans wordChoices)
            </Typography>
          )}
        </Box>
      );
    }

    case 'association':
      return (
        <Box>
          {content.enonce && (
            <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          )}
          {(content.pairs || []).map((pair, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">{pair.left}</Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: 'center' }}>
                  <Typography color="text.secondary">→</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2">{pair.right}</Typography>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>
      );

    case 'categorisation':
      return (
        <Box>
          {content.enonce && (
            <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          )}
          <SpokenModeBadge content={content} />
          {(content.categories || []).map((cat) => (
            <Paper key={cat.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>{cat.label}</Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {(content.items || [])
                  .filter((item) => item.correctCategoryId === cat.id)
                  .map((item, i) => (
                    <GestureItemChip key={i} item={item} />
                  ))}
              </Box>
            </Paper>
          ))}
        </Box>
      );

    case 'speed_sort':
      return (
        <Box>
          {content.enonce && (
            <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          )}
          <SpokenModeBadge content={content} />
          <Grid container spacing={2}>
            {['left', 'right'].map((side) => (
              <Grid item xs={6} key={side}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {side === 'left' ? content.leftLabel : content.rightLabel}
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {(content.sortItems || [])
                      .filter((item) => item.correctSide === side)
                      .map((item, i) => (
                        <GestureItemChip key={i} item={item} />
                      ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      );

    case 'comprehension': {
      const passage = content.passage || {};
      const questions = content.questions || [];
      return (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            {passage.title && (
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{passage.title}</Typography>
            )}
            <Typography variant="body2" whiteSpace="pre-wrap">{passage.text}</Typography>
          </Paper>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {questions.length} question{questions.length > 1 ? 's' : ''}
          </Typography>
          {questions.map((q, i) => {
            const itemType = q.itemType || 'qcm';
            return (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Box display="flex" gap={0.5} alignItems="center" mb={0.5} flexWrap="wrap">
                  <Typography variant="body2" fontWeight="bold">Q{i + 1}. {q.prompt}</Typography>
                  <Chip label={itemType} size="small" variant="outlined" color="secondary" />
                  {q.notion && <Chip label={q.notion} size="small" variant="outlined" />}
                  {q.difficulty && <Chip label={q.difficulty} size="small" variant="outlined" color="info" />}
                </Box>

                {itemType === 'qcm' && (
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {(q.options || []).map((option, idx) => (
                      <Chip key={idx} label={option} size="small" color={option === q.answer ? 'success' : 'default'} />
                    ))}
                  </Box>
                )}

                {itemType === 'span' && (
                  <Chip label={`réponse (extrait) : « ${q.answer} »`} size="small" color="success" />
                )}

                {itemType === 'cloze' && (
                  <Box>
                    <Typography variant="body2" whiteSpace="pre-wrap" mb={0.5}>{q.text}</Typography>
                    {(q.blanks || []).map((b, bi) => (
                      <Box key={bi} display="flex" gap={0.5} flexWrap="wrap" alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">Trou {bi + 1} :</Typography>
                        <Chip label={b.answer} size="small" color="success" />
                        {(b.distractors || []).map((d, di) => (
                          <Chip key={di} label={d} size="small" />
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}

                {itemType === 'association' && (
                  <Box>
                    {(q.pairs || []).map((p, pi) => (
                      <Typography key={pi} variant="body2">
                        <b>{p.left}</b> → {p.right}
                      </Typography>
                    ))}
                  </Box>
                )}

                {q.sourceSpan && (
                  <Typography variant="caption" display="block" mt={0.5} fontStyle="italic" color="text.secondary">
                    span : {q.sourceSpan}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      );
    }

    default:
      return (
        <Typography variant="body2" color="text.secondary">
          Aperçu non disponible pour ce type d'exercice.
        </Typography>
      );
  }
};

export default ExercisePreview;
